import { DocumentIcon } from "@heroicons/react/20/solid"
import { clsx } from "clsx"
import { ChangeEventHandler, DragEventHandler, useEffect, useState } from "react"

export enum AcceptFileType {
    XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    XLS = "application/vnd.ms-excel",
    CSV = "text/csv",
    TXT = "text/plain",
    TSV = "text/tab-separated-values",
    JSON = "application/json",
}

export const AcceptFileTypeLabels = {
    [AcceptFileType.CSV]: "CSV",
    [AcceptFileType.JSON]: "JSON",
    [AcceptFileType.TSV]: "TSV",
    [AcceptFileType.TXT]: "TXT",
    [AcceptFileType.XLS]: "XLS",
    [AcceptFileType.XLSX]: "XLSX",
}

export const AcceptFileTypeExtensions = {
    [AcceptFileType.CSV]: ".csv",
    [AcceptFileType.JSON]: ".json",
    [AcceptFileType.TSV]: ".tsv",
    [AcceptFileType.TXT]: ".txt",
    [AcceptFileType.XLS]: ".xls",
    [AcceptFileType.XLSX]: ".xlsx",
}

export function convertExtensionToMimeType(extension: string): AcceptFileType {
    const pair = Object.entries(AcceptFileTypeExtensions).find(([, ext]) => ext === extension)

    if (!pair) {
        throw new Error(`Unknown extension: ${extension}`)
    }

    return pair[0] as AcceptFileType
}

export interface Props {
    upload: (files: FileList) => void
    label?: string
    multiple?: boolean
    accepts?: AcceptFileType[]
}

export function FileField({ accepts, label, multiple, upload }: Props) {
    const [dragInProgress, setDragInProgress] = useState(false)

    if (accepts == null) {
        accepts = [AcceptFileType.XLSX, AcceptFileType.XLS, AcceptFileType.CSV]
    }

    useEffect(() => {
        // Use IntersectionObserver to determine when the element is visible in the viewport
        // and then preload xlsx using "import('xlsx')"

        const options = {
            root: null,
            rootMargin: "0px",
            threshold: 0,
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    void import("xlsx")
                }
            })
        }, options)

        const target = document.querySelector("#file-upload")

        if (target) {
            observer.observe(target)
        }

        return () => {
            if (target) {
                observer.unobserve(target)
            }
        }
    }, [])

    const onDragEnter: DragEventHandler<HTMLDivElement> = (ev) => {
        ev.preventDefault()
        setDragInProgress(true)
    }

    const onDragLeave: DragEventHandler<HTMLDivElement> = (ev) => {
        ev.preventDefault()
        setDragInProgress(false)
    }

    const onDrop: DragEventHandler<HTMLDivElement> = (ev) => {
        ev.preventDefault()
        setDragInProgress(false)
        const dt = ev.dataTransfer
        upload(dt.files)
    }

    const dragOverHandler: DragEventHandler<HTMLDivElement> = (ev) => {
        ev.preventDefault()
        setDragInProgress(true)
    }

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (ev) => {
        const files = ev.target.files
        if (files) {
            upload(files)
        }
    }

    return (
        <div className="col-span-full">
            {label && (
                <label
                    htmlFor="file-upload"
                    className="text-md block leading-6 font-medium text-gray-900 dark:text-gray-100"
                >
                    {label}
                </label>
            )}
            <div
                className={clsx(
                    "mt-2 flex min-h-80 flex-col justify-center rounded-lg border-2 border-dashed px-6 py-10",
                    dragInProgress ? "border-primary-600" : "border-gray-900/25 dark:border-gray-300/25"
                )}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onDragOver={dragOverHandler}
            >
                {" "}
                <div className="mx-auto text-center">
                    <DocumentIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-700" aria-hidden="true" />
                    <div className="mt-4 flex text-sm leading-6 text-gray-600 dark:text-gray-300">
                        <label
                            htmlFor="file-upload"
                            className="text-primary-700 focus-within:ring-primary-600 hover:text-primary-800 relative cursor-pointer rounded-md font-semibold focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-none"
                        >
                            <span>Upload a file</span>
                            <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                className="sr-only"
                                onChange={handleInputChange}
                                accept={accepts.map((a) => AcceptFileTypeExtensions[a]).join(", ")}
                                multiple={multiple ?? false}
                            />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs leading-5 text-gray-600 dark:text-gray-300">
                        {accepts.map((a) => AcceptFileTypeLabels[a]).join(", ")}
                    </p>
                </div>
            </div>
        </div>
    )
}
